import React, { useMemo, useState } from 'react'
import { cloneDeep } from 'lodash'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Text from 'components_new/atoms/Text'

import AdvancedDialog from 'components_new/molecules/AdvancedDialog'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import EditKpiSection from './EditKpiSection'

import { KPI_UNIT } from 'types/Enums'

import { formatBlock, getEmptyBlock, isValidBlock } from './utils'
import {
  TMetricCalculationBlock,
  TPutMetricCalculationBlock
} from 'types/GlobalMetric'
import { ICustomEconomyMetric } from 'types/GlobalMetric'
import { KpiVariable, KpiVariableWithChildren } from 'types/GlobalKpiVariable'
import { useTranslation } from 'language/translator'

interface EditKpiDialogProps {
  formattedVariables: KpiVariableWithChildren[]
  kpi: ICustomEconomyMetric
  onClose: () => void
  onRemoveDefinition?: () => void
  onSave: (body: {
    name: string
    blocks: TPutMetricCalculationBlock
  }) => void
  open: boolean
  variables: KpiVariable[]
  templates: ICustomEconomyMetric[]
}

const EditKpiDialog = (props: EditKpiDialogProps) => {
  const {
    formattedVariables,
    kpi,
    onClose,
    onRemoveDefinition,
    onSave,
    open,
    variables,
    templates
  } = props

  const { t } = useTranslation()

  const [editFormula, setEditFormula] = useState<TMetricCalculationBlock>(
    kpi.custom_calculation_block
      ? cloneDeep(kpi.custom_calculation_block)
      : getEmptyBlock()
  )
  const [editName, setEditName] = useState<string>(kpi.title.sv)
  const [confirmRemove, setConfirmRemove] = useState<boolean>(false)

  const allowRemoveDefinition =
    Boolean(onRemoveDefinition) && kpi.homepal && !!kpi.custom_calculation_block

  const validBlock = useMemo(() => isValidBlock(editFormula), [editFormula])
  const validName = useMemo(() => {
    return !templates.find((t) => t.title.sv === editName && t.id !== kpi.id)
  }, [editName])

  return (
    <AdvancedDialog
      fullWidth={true}
      maxWidth="lg"
      title={t('metrics', 'edit_metric')}
      open={open}
      onClose={onClose}
      actions={
        <>
          {allowRemoveDefinition ? (
            <>
              <Button
                color="error"
                variant="text"
                onClick={() => setConfirmRemove(true)}
              >
                {t('metrics', 'remove_definition')}
              </Button>
              <Box sx={{ flex: 1 }} />
            </>
          ) : null}
          <Button variant="text" onClick={onClose}>
            {t('global', 'cancel')}
          </Button>
          <Button
            disabled={!editName || !validBlock || !validName}
            variant="contained"
            onClick={() => {
              onSave({ name: editName, blocks: formatBlock(editFormula) })
            }}
          >
            {t('global', 'save')}
          </Button>
        </>
      }
    >
      <EditKpiSection
        formattedVariables={formattedVariables}
        editFormula={editFormula}
        editName={editName}
        editUnit={kpi.unit as KPI_UNIT}
        setEditFormula={setEditFormula}
        setEditName={setEditName}
        validName={validName}
        variables={variables}
        templates={templates.filter((template) => template.id !== kpi.id)}
        homepalKpi={kpi.homepal}
      />
      <SimpleDialog
        actions={
          <>
            <Button onClick={() => setConfirmRemove(false)} variant="text">
              {t('global', 'cancel')}
            </Button>
            <Button
              color="error"
              onClick={() => {
                setConfirmRemove(false)
                onRemoveDefinition?.()
              }}
            >
              {t('metrics', 'remove_definition')}
            </Button>
          </>
        }
        onClose={() => setConfirmRemove(false)}
        open={confirmRemove}
        title={t('metrics', 'remove_definition_title')}
      >
        <Text>
          <b>{kpi.title.sv}</b>: {t('metrics', 'remove_definition_body')}
        </Text>
      </SimpleDialog>
    </AdvancedDialog>
  )
}

export default EditKpiDialog
